import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import {
  adsAllowed,
  getRegion,
  readAdsConsent,
  startPixel,
  stopPixel,
  subscribeCookieSettingsOpen,
  writeAdsConsent,
  type RegionInfo,
} from "@/lib/adsConsent";

/**
 * Cookie consent strip — same visual language as the newsletter strip.
 *
 * Shows only to visitors in regions that require permission before an
 * advertising tracker runs (or when the region can't be resolved). Accepting
 * starts the Meta Pixel right away; refusing keeps it off. The choice is
 * stored with the time it was made and the notice version it was made against,
 * and "Cookie settings" reopens this same strip so it can be changed later.
 */
const CookieConsentStrip = () => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [region, setRegion] = useState<RegionInfo | null>(null);

  useEffect(() => {
    let active = true;

    // Already answered -> nothing to ask; just make the pixel match the answer.
    if (readAdsConsent()) {
      adsAllowed().then((allowed) => {
        if (!active) return;
        if (allowed) startPixel();
        else stopPixel();
      });
      return () => {
        active = false;
      };
    }

    getRegion().then((info) => {
      if (!active) return;
      setRegion(info);
      // Regulated or unresolved -> ask. Open regions run without a banner.
      if (info.regulated) setOpen(true);
      else startPixel();
    });

    return () => {
      active = false;
    };
  }, []);

  // "Cookie settings" reopens the strip so the choice can be withdrawn.
  useEffect(() => subscribeCookieSettingsOpen(() => setOpen(true)), []);

  const accept = useCallback(() => {
    writeAdsConsent("granted", region?.loc || "unknown");
    startPixel();
    setOpen(false);
  }, [region]);

  const refuse = useCallback(() => {
    writeAdsConsent("denied", region?.loc || "unknown");
    stopPixel();
    setOpen(false);
  }, [region]);

  if (!open) return null;

  return (
    <section
      role="region"
      aria-label={t("consent.aria", "Cookie settings")}
      className="fixed inset-x-0 bottom-[76px] lg:bottom-0 z-[60] bg-gradient-primary shadow-2xl"
    >
      <div className="container mx-auto px-4 lg:px-8 py-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6">
          <div className="flex items-start gap-3 text-primary-foreground min-w-0">
            <Cookie className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h3 className="font-bold text-sm leading-tight">
                {t("consent.title", "Cookies")}
              </h3>
              <p className="text-xs text-primary-foreground/75 leading-snug mt-1">
                {t(
                  "consent.body",
                  "We use our own and third-party cookies to keep the site working, measure how it is used and optimise our ads on Facebook and Instagram. You can accept or refuse; you can change your mind any time from Cookie settings."
                )}{" "}
                <Link
                  to="/privacy"
                  className="underline underline-offset-2 text-primary-foreground"
                >
                  {t("consent.privacy_link", "Privacy Policy")}
                </Link>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 lg:ml-auto">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={refuse}
              className="h-9 bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 font-semibold"
            >
              {t("consent.refuse", "Refuse")}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={accept}
              className="h-9 bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold"
            >
              {t("consent.accept", "Accept")}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CookieConsentStrip;
